import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { CheckboxLine, HpButton, HpCardHeader, HpTag, TableScroll } from '@hp/ui';
import { useCan } from '../../api/me';
import { trpc, trpcClient } from '../../api/trpc';
import { StatusBadge } from '../../components/badges';
import { ErrorAlert } from '../../components/error-alert';
import { RelativeTime } from '../../components/relative-time';
import { Disclosure } from '../../components/ui/disclosure';
import { Spinner } from '../../components/ui/icons';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';
import { AvailabilityBadge } from './availability-badge';
import {
  invalidateProjects,
  invalidateSite,
  useProjectSite,
  type ScanResult,
  type SiteChange,
  type SiteImportSummary,
} from './queries';

// Czech business amounts: CZK is formatted the Czech way in both UI languages.
const czk = new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 });

export function SiteSyncSection({
  projectId,
  websiteUrl,
  hasAddress,
}: {
  projectId: string;
  websiteUrl: string | null;
  /** When the project has no address yet, the scan's address import defaults to on. */
  hasAddress: boolean;
}) {
  const { t } = useTranslation(['projects', 'common']);
  const site = useProjectSite(projectId);
  const canEdit = useCan()('projects.edit');
  const [scan, setScan] = useState<ScanResult | null>(null);
  const [importPhotos, setImportPhotos] = useState(true);
  const [importAddress, setImportAddress] = useState(false);
  const [importContact, setImportContact] = useState(true);
  const [importNotice, setImportNotice] = useState<string | null>(null);

  const avail = (v: string | null) => t(`availability.${v ?? 'available'}`, { defaultValue: v ?? '—' });

  function describeChange(change: SiteChange): { what: string; detail: React.ReactNode } {
    const price = (v: string | null) => (v === null ? '—' : czk.format(Number(v)));
    const what = t(`site.change.${change.kind}`);
    switch (change.kind) {
      case 'price':
        return {
          what,
          detail: (
            <>
              <s className="bo-muted bo-mono">{price(change.oldValue)}</s>{' '}
              <strong className="bo-mono">→ {price(change.newValue)}</strong>
            </>
          ),
        };
      case 'availability':
        return {
          what,
          detail: (
            <>
              <s className="bo-muted">{avail(change.oldValue)}</s> <strong>→ {avail(change.newValue)}</strong>
            </>
          ),
        };
      case 'unit_added':
        return {
          what,
          detail: change.payload ? (
            <>
              {change.payload.disposition ?? '?'}
              {change.payload.price != null && <> · {czk.format(change.payload.price)}</>} ·{' '}
              {avail(change.payload.availability)} — {t('site.change.applyToAdd')}
            </>
          ) : null,
        };
      case 'unit_removed':
        return {
          what,
          detail: <>{t('site.change.applyToMark', { availability: avail(change.newValue) })}</>,
        };
    }
  }

  const runScan = useMutation(
    trpc.scrape.scan.mutationOptions({
      onSuccess: (res) => {
        setScan(res);
        setImportPhotos((res.projectInfo?.photoUrls.length ?? 0) > 0);
        // Filling an empty address is the expected flow; overwriting an existing one is opt-in.
        setImportAddress(!hasAddress && Boolean(res.projectInfo?.address));
        setImportContact(Boolean(res.projectInfo?.contact?.name));
      },
    }),
  );

  const saveBlueprint = useMutation({
    mutationFn: async (importUnits: boolean) => {
      if (!scan) throw new Error('No scan to save');
      const saved = await trpcClient.scrape.saveBlueprint.mutate({ projectId, blueprint: scan.blueprint, importUnits });
      const info = scan.projectInfo;
      const wantsImport = info && (importPhotos || importAddress || importContact);
      let summary: SiteImportSummary | null = null;
      if (wantsImport) {
        summary = await trpcClient.scrape.importSiteData.mutate({
          projectId,
          photoUrls: importPhotos ? info.photoUrls : [],
          address: importAddress ? (info.address ?? null) : null,
          yearOfCompletion: importAddress ? (info.yearOfCompletion ?? null) : null,
          contact: importContact && info.contact?.name ? { ...info.contact, name: info.contact.name } : null,
        });
      }
      return { saved, summary };
    },
    onSuccess: ({ saved, summary }) => {
      setScan(null);
      const parts = [t('site.notice.saved', { count: saved.unitsSeen })];
      if (saved.unitsImported > 0) parts.push(t('site.notice.unitsImported', { count: saved.unitsImported }));
      if (summary) {
        if (summary.photos.imported > 0) {
          parts.push(t('site.notice.photosImported', { count: summary.photos.imported }));
        }
        if (summary.photos.skippedExisting > 0) {
          parts.push(t('site.notice.photosExisting', { count: summary.photos.skippedExisting }));
        }
        if (summary.photos.failed.length > 0) {
          parts.push(t('site.notice.photosFailed', { count: summary.photos.failed.length }));
        }
        if (summary.contactId !== null) parts.push(t('site.notice.contactAdded'));
        if (summary.updatedFields.length > 0) {
          parts.push(t('site.notice.updatedFields', { fields: summary.updatedFields.join(', ') }));
        }
      }
      setImportNotice(parts.join(' · '));
      window.setTimeout(() => setImportNotice(null), 8000);
      return Promise.all([invalidateSite(), invalidateProjects()]);
    },
  });

  const checkNow = useMutation(trpc.scrape.checkNow.mutationOptions({ onSuccess: invalidateSite }));

  const resolve = useMutation({
    mutationFn: ({ changeId, action }: { changeId: number; action: 'apply' | 'dismiss' }) =>
      action === 'apply'
        ? trpcClient.scrape.applyChange.mutate({ projectId, changeId })
        : trpcClient.scrape.dismissChange.mutate({ projectId, changeId }),
    onSuccess: () => Promise.all([invalidateSite(), invalidateProjects()]),
  });

  const resolveBusy = (changeId: number, action: 'apply' | 'dismiss'): boolean =>
    resolve.isPending && resolve.variables?.changeId === changeId && resolve.variables.action === action;

  if (!websiteUrl) {
    return (
      <section className="hp-card">
        <HpCardHeader title={t('site.title')} icon="globe" />
        <p className="hp-card__body bo-muted">{t('site.noWebsite')}</p>
      </section>
    );
  }

  const data = site.data;
  const checkActive = data?.runs.some((r) => r.status === 'queued' || r.status === 'running') ?? false;
  const lastCompleted = data?.runs.find((r) => r.status === 'completed');
  // When the most recent finished check failed (site down, timeout…), say so in
  // the status line instead of only inside the collapsed "Check history".
  const lastFinished = data?.runs.find((r) => r.status === 'completed' || r.status === 'failed');
  const lastFailed = lastFinished?.status === 'failed' ? lastFinished : null;

  return (
    <section className="hp-card">
      <HpCardHeader
        title={t('site.title')}
        subtitle={
          <a href={websiteUrl} target="_blank" rel="noreferrer" className="hp-detail-action hp-detail-action--text">
            {websiteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')} ↗
          </a>
        }
        icon="globe"
      >
        {canEdit && data?.blueprint && !checkActive && (
          <HpButton icon="refresh" busy={checkNow.isPending} onClick={() => checkNow.mutate({ projectId })}>
            {t('site.checkNow')}
          </HpButton>
        )}
        {checkActive && <Spinner>{t('site.checking')}</Spinner>}
        {canEdit && data && !scan && (
          <HpButton
            variant={data.blueprint ? 'quiet' : 'primary'}
            icon="spark"
            busy={runScan.isPending}
            disabled={!data.aiConfigured}
            title={data.aiConfigured ? t('site.scanTitle') : t('site.aiNotConfigured')}
            onClick={() => runScan.mutate({ projectId })}
          >
            {runScan.isPending ? t('site.scanning') : data.blueprint ? t('site.rescan') : t('site.scan')}
          </HpButton>
        )}
      </HpCardHeader>

      <div className="hp-card__body bo-sections">
        <ErrorAlert error={site.error ?? runScan.error ?? saveBlueprint.error ?? checkNow.error ?? resolve.error} />
        {importNotice && (
          <p className="bo-notice" role="status">
            {importNotice}
          </p>
        )}

        {/* Blueprint status line */}
        {data?.blueprint && (
          <p className="bo-status-line">
            {data.blueprint.status === 'broken' ? (
              <>
                <HpTag tone="danger">{t('site.blueprintBroken')}</HpTag>
                <span>
                  {t('site.rescanToRepair', { error: data.blueprint.lastError ?? t('site.structureChanged') })}
                </span>
              </>
            ) : lastFailed ? (
              <>
                <HpTag tone="warning">{t('site.lastCheckFailed')}</HpTag>
                <span>
                  {t('site.retriedAutomatically', { error: lastFailed.error ?? t('site.unknownError') })}{' '}
                  {lastCompleted ? (
                    <Trans
                      t={t}
                      i18nKey="site.lastSuccess"
                      components={{ time: <RelativeTime value={lastCompleted.finishedAt} /> }}
                    />
                  ) : (
                    t('site.noSuccessYet')
                  )}
                </span>
              </>
            ) : (
              <>
                <HpTag tone="success">{t('site.monitoring')}</HpTag>
                <span>
                  {t('site.checkedEvery', { hours: data.intervalHours })}{' '}
                  {lastCompleted ? (
                    <Trans
                      t={t}
                      i18nKey="site.lastCheck"
                      values={{ units: lastCompleted.stats?.unitsScraped ?? '?' }}
                      components={{ time: <RelativeTime value={lastCompleted.finishedAt} /> }}
                    />
                  ) : (
                    t('site.firstCheckSoon')
                  )}
                </span>
              </>
            )}
          </p>
        )}
        {data && !data.blueprint && !scan && (
          <p className="bo-muted">
            <Trans t={t} i18nKey="site.notMonitored" components={{ strong: <strong /> }} />
          </p>
        )}

        {/* Scan wizard: preview what the blueprint reads before trusting it */}
        {scan && (
          <div className="bo-subform">
            <h3 className="bo-subform__title">{t('site.previewTitle', { count: scan.units.length })}</h3>
            {scan.blueprint.notes && <p className="bo-muted">{scan.blueprint.notes}</p>}
            <div className="bo-subform__table">
              <TableScroll label={t('site.previewTableLabel')}>
                <BrutTable>
                  <BrutTheadRow>
                    <th>{t('site.unit')}</th>
                    <th>{t('units.columns.disposition')}</th>
                    <th className="bo-cell-num">{t('units.columns.price')}</th>
                    <th>{t('units.columns.availability')}</th>
                  </BrutTheadRow>
                  <tbody>
                    {scan.units.map((u) => (
                      <tr key={u.title}>
                        <td className="bo-strong">{u.title}</td>
                        <td>{u.disposition ?? <span className="bo-muted">—</span>}</td>
                        <td className="bo-cell-num">
                          {u.price != null ? czk.format(u.price) : <span className="bo-muted">—</span>}
                        </td>
                        <td>
                          <AvailabilityBadge availability={u.availability} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </BrutTable>
              </TableScroll>
            </div>
            {scan.projectInfo && (
              <div className="bo-sections">
                <h4 className="bo-subform__title">{t('site.alsoFound')}</h4>
                {scan.projectInfo.photoUrls.length > 0 && (
                  <div className="bo-sections">
                    <CheckboxLine checked={importPhotos} onChange={(e) => setImportPhotos(e.target.checked)}>
                      {t('site.importPhotos', { count: scan.projectInfo.photoUrls.length })}
                    </CheckboxLine>
                    <div className="bo-filmstrip">
                      {scan.projectInfo.photoUrls.slice(0, 12).map((url) => (
                        // preview straight from the developer's CDN; stored copies are made on import
                        <img key={url} src={url} alt="" />
                      ))}
                      {scan.projectInfo.photoUrls.length > 12 && (
                        <span className="bo-muted bo-nowrap">
                          {t('site.morePhotos', { count: scan.projectInfo.photoUrls.length - 12 })}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {(scan.projectInfo.address || scan.projectInfo.yearOfCompletion != null) && (
                  <CheckboxLine checked={importAddress} onChange={(e) => setImportAddress(e.target.checked)}>
                    {hasAddress ? t('site.overwriteAddress') : t('site.setAddress')}
                    <span className="bo-muted">
                      {' '}
                      — {scan.projectInfo.address}
                      {scan.projectInfo.yearOfCompletion != null && (
                        <> · {t('site.completionYear', { year: scan.projectInfo.yearOfCompletion })}</>
                      )}
                    </span>
                  </CheckboxLine>
                )}
                {scan.projectInfo.contact?.name && (
                  <CheckboxLine checked={importContact} onChange={(e) => setImportContact(e.target.checked)}>
                    {t('site.addContact')}
                    <span className="bo-muted">
                      {' '}
                      — {scan.projectInfo.contact.name}
                      {scan.projectInfo.contact.email && <> · {scan.projectInfo.contact.email}</>}
                      {scan.projectInfo.contact.phone && <> · {scan.projectInfo.contact.phone}</>}
                      {scan.projectInfo.contact.companyName && <> · {scan.projectInfo.contact.companyName}</>}
                    </span>
                  </CheckboxLine>
                )}
              </div>
            )}
            <div className="bo-row">
              <HpButton
                variant="primary"
                icon="check"
                busy={saveBlueprint.isPending && saveBlueprint.variables === true}
                disabled={saveBlueprint.isPending}
                onClick={() => saveBlueprint.mutate(true)}
              >
                {saveBlueprint.isPending ? t('site.saving') : t('site.saveAndImport')}
              </HpButton>
              <HpButton
                variant="secondary"
                busy={saveBlueprint.isPending && saveBlueprint.variables === false}
                disabled={saveBlueprint.isPending}
                onClick={() => saveBlueprint.mutate(false)}
              >
                {t('site.saveOnly')}
              </HpButton>
              <HpButton onClick={() => setScan(null)}>{t('site.discard')}</HpButton>
            </div>
          </div>
        )}

        {/* Pending changes review queue */}
        {data && data.pendingChanges.length > 0 && (
          <div className="bo-sections">
            <h3 className="bo-subform__title">{t('site.pendingTitle', { count: data.pendingChanges.length })}</h3>
            <ul className="bo-people">
              {data.pendingChanges.map((change) => {
                const { what, detail } = describeChange(change);
                return (
                  <li key={change.id} className="bo-person bo-change">
                    <div className="bo-row">
                      <HpTag tone="info">{what}</HpTag>
                      <strong>{change.unitTitle}</strong>
                      <span>{detail}</span>
                      <RelativeTime value={change.createdAt} className="bo-muted" />
                      <span className="bo-grow-end bo-row">
                        <HpButton
                          className="bo-button--sm"
                          variant="primary"
                          icon="check"
                          busy={resolveBusy(change.id, 'apply')}
                          disabled={resolve.isPending || !canEdit}
                          onClick={() => resolve.mutate({ changeId: change.id, action: 'apply' })}
                        >
                          {t('common:actions.apply')}
                        </HpButton>
                        <HpButton
                          className="bo-button--sm"
                          busy={resolveBusy(change.id, 'dismiss')}
                          disabled={resolve.isPending || !canEdit}
                          onClick={() => resolve.mutate({ changeId: change.id, action: 'dismiss' })}
                        >
                          {t('common:actions.dismiss')}
                        </HpButton>
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {data?.blueprint && data.blueprint.status === 'active' && data.pendingChanges.length === 0 && !scan && (
          <p className="bo-muted">{t('site.noPending')}</p>
        )}
      </div>

      {/* Check history */}
      {data && data.runs.length > 0 && (
        <div className="bo-card-foot">
          <Disclosure title={t('site.historyTitle')} caption={t('site.historyCaption', { count: data.runs.length })}>
            <ul className="bo-run-list">
              {data.runs.map((run) => (
                <li key={run.id}>
                  <StatusBadge status={run.status} />
                  <span className="bo-muted">{t(`site.trigger.${run.trigger}`, { defaultValue: run.trigger })}</span>
                  <RelativeTime value={run.finishedAt ?? run.createdAt} className="bo-muted" />
                  {run.status === 'completed' && run.stats && (
                    <span className="bo-mono bo-muted">
                      {t('site.runStats', { units: run.stats.unitsScraped, changes: run.stats.changesNew ?? 0 })}
                    </span>
                  )}
                  {run.error && <span className="bo-hint bo-hint--danger">{run.error}</span>}
                </li>
              ))}
            </ul>
          </Disclosure>
        </div>
      )}
    </section>
  );
}
